<script setup lang="ts">
import { nextTick, ref } from 'vue'
import { FilterField, Params, TableField } from "../../../../components/table/table";
import { Permission } from '../../../../types/auth'
import ListTemplate from "../../../../components/ui/common/ListTemplate.vue";
import LedgerEntriesForm from './LedgerEntriesForm.vue';
import Buttons from '../../../../components/ui/form/Buttons.vue';
import LedgerEntriesImportExcel from './LedgerEntriesImportExcel.vue';
import DateInput from '../../../../components/ui/form/DateInput.vue';
import { api } from '../../../../api/Api';
//import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { isNumeric } from '../../../../utils/CommonFunctions';
import SearchSelect from '../../../../components/ui/form/SearchSelect.vue';

withDefaults(defineProps<{
  showHeader?: boolean,
  showQuickTour?: boolean,
  showTitle?: boolean,
  showNextButton?: boolean  
}>(), {
  showHeader: true,
  showQuickTour: true,
  showTitle: true
})

const endPoint = '/clients/finance/accounting/ledger-entries/'
const title = 'Ledger Entries'
const selectedData = ref<any | null>(null)
const showForm = ref(false)
const formMode = ref<'create' | 'edit' | 'view' | 'showQuickView' | 'toggleStatus'>('view')
const refreshData = ref(false)

const permissions = ref<Permission[]>()
const getPermissions = async (perm: Permission[]) => {
  permissions.value = perm
}

const fields: TableField[] = [
  { name: 'date', caption: 'Name', sortable: true, fieldType: 'date' },
  { name: 'chart_of_account', caption: 'Account', sortable: true, fieldType: 'text' },
  { name: 'currency', caption: 'Currency', sortable: true, fieldType: 'text' },
  { name: 'exchange_rate', caption: 'Exchange Rate', sortable: true, fieldType: 'number' },
  { name: 'dr', caption: 'Debit', sortable: true, fieldType: 'number' },
  { name: 'cr', caption: 'Credit', sortable: true, fieldType: 'number' },
  { name: 'record_type', caption: 'Record Type', sortable: true, fieldType: 'text' },
  { name: 'reference', caption: 'Reference', sortable: true, fieldType: 'text' },
  { name: 'remarks', caption: 'Remarks', sortable: true, fieldType: 'text' },  
  { name: 'reconciled', caption: 'Reconciled', sortable: false, fieldType: 'status' },
  { name: 'reconciliation_date', caption: 'Reconciled On', sortable: true, fieldType: 'date' },
]

const filterFields: FilterField[] = []

const changeMode = (
  mode: "create" | "edit" | "view" | "showQuickView" | "toggleStatus"
) => {
  formMode.value = mode;
  showForm.value = mode === "view" || mode === "edit" || mode === "create";
};

const handleData = (data : any) => {
  selectedData.value = data
}

const selectLists = ref([]);
const handleLists = (data: any) => {
  selectLists.value = data;
};

const showImportExcel = ref(false);
const showExportExcel = ref(false);
const excelFromDate = ref<string>('');
const excelToDate = ref<string>('');

const exportLedger = async () => {
  const response = await api.post(
      (endPoint.slice(-1) == "/"
        ? endPoint
        : endPoint + "/") + "export-ledger-excel",
      {
        from_date: excelFromDate.value,
        to_date: excelToDate.value,
        chart_of_accounts_id: chart_of_accounts_id.value,
      }
    );
    if (response.success == true) {
      //console.log('Exported Successfully', response.data);
      exportToExcel(response.data);
      showExportExcel.value = false;
    }
};

const exportToExcel = (data: any[]) => {

  const columns = Object.keys(data[0])
      .filter((key) => key !== "hasChild") // Exclude hasChild from display
      .map((field) => ({
        field,
        header:
          field.charAt(0).toUpperCase() +
          field
            .slice(1)
            .replace(/([A-Z])/g, " $1")
            .trim()
            .replace("_", " "), // Convert camelCase to Title Case
      }));

  const ws: XLSX.WorkSheet = {};
  const range = {
    s: { c: 0, r: 0 },
    e: { c: columns.length - 1, r: data.length },
  };

  // Add headers
  columns.forEach((col, colIdx) => {
    const cell: XLSX.CellObject = {
      v: col.header,
      t: "s", // string type for headers
    };
    const cellRef = XLSX.utils.encode_cell({ r: 0, c: colIdx });
    ws[cellRef] = cell;
  });

  // Add data with proper types
  data.forEach((row, rowIdx) => {
    columns.forEach((col, colIdx) => {
      const cellRef = XLSX.utils.encode_cell({ r: rowIdx + 1, c: colIdx });
      const value = row[col.field];

      let cell: XLSX.CellObject;

      if (typeof value === "number" || isNumeric(value)) {
        cell = { v: value, t: "n" }; // number type
      } else if (typeof value === "boolean") {
        cell = { v: value, t: "b" }; // boolean type
      } else if (value instanceof Date) {
        cell = { v: value, t: "d" }; // date type
      } else {
        cell = { v: value, t: "s" }; // string type
      }

      ws[cellRef] = cell;
    });
  });

  ws["!ref"] = XLSX.utils.encode_range(range);

  // Create workbook and export
  const wb = XLSX.utils.book_new();
  let fileName = "ledger_entries_";
  if (fileName.length > 30) {
    fileName = fileName.substring(0, 30);
  }
  XLSX.utils.book_append_sheet(wb, ws, fileName);
  // Use only the first 30 characters of the title if it exceeds 30
  
  XLSX.writeFile(wb, fileName + ".xlsx");
};


const chart_of_accounts_id = ref<string | null>(null);
const from_date = ref<string>('');
const to_date = ref<string>('');


const page = ref<number>(0);
const filterRecords = () => {
  page.value = 1;
  refreshData.value = !refreshData.value;
  nextTick(() => {
    page.value = 0;
  });
};

</script>

<template>
  <ListTemplate :endPoint = "endPoint" :title = "title" :fields = "fields" :actions = "[]" :filterFields ="filterFields" :page="page"
  :tabList = "[]" :refreshData = "refreshData" @mode="changeMode" @show="showForm = true" :showForm="showForm" @lists="handleLists"
  @data="handleData" :show-header="showHeader" :show-quick-tour="showQuickTour" @permissions="getPermissions" :show-title="showTitle"
  :params="[
    { field: 'chart_of_accounts_id', value: chart_of_accounts_id } as Params,
    { field: 'from_date', value: from_date } as Params,
    { field: 'to_date', value: to_date } as Params,
  ]">
  <template #header-actions>
    <Buttons
      caption="Import using Excel Files"
      icon="Import"
      @click="showImportExcel = true"/>
      <div style="position:relative; display:inline-block;">
        <Buttons
          caption="Export to Excel"
          icon="Sheet"
          @click="showExportExcel = true"/>
        <div v-if="showExportExcel" style="position:absolute; right:0; margin-top:8px; z-index:50;">
          <div style="background:#fff; border:1px solid #e5e7eb; padding:12px; border-radius:6px; box-shadow:0 8px 16px rgba(0,0,0,0.1); min-width:320px;">
            <!-- DateRangeInput shows two calendars (start + end) -->
             <SearchSelect
        endPoint="/clients/finance/accounting/chart-of-accounts"
        label="Account"
        v-model="chart_of_accounts_id"
        />
            <DateInput label="From Date" v-model="excelFromDate" />
            <DateInput label="To Date" v-model="excelToDate" />
            <div style="display:flex; justify-content:flex-end; gap:8px; margin-top:10px;">
              <Buttons caption="Cancel" @click="showExportExcel = false" />
              <Buttons caption="Export" v-if="excelFromDate && excelToDate" @click="exportLedger" />
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #table-header-actions>
        <SearchSelect
        endPoint="/clients/finance/accounting/chart-of-accounts"
        label="Account"
        v-model="chart_of_accounts_id"
        />
        <DateInput label="From Date" v-model="from_date" />
        <DateInput label="To Date" v-model="to_date" />
        <Buttons type="info" caption="Filter" icon="Filter" class="h-[40px]" @click="filterRecords" />
    </template>
  <template #form-view>
    <LedgerEntriesForm
      :permissions="permissions"
      v-if="showForm"
      :data="selectedData"
      :lists="selectLists"
      :end-point="endPoint"
      :title="title"
      :mode="formMode"
      @save="refreshData = !refreshData; changeMode('view')"
      @close="showForm = !showForm"
    />
    <LedgerEntriesImportExcel
    :endPoint="endPoint"
      v-if="showImportExcel"
      @close="showImportExcel = false"
    />
  </template>
  </ListTemplate>
</template>